import { fetchPutResponse } from "../../utilities/ApiUtilities";
import { formatDate, parseDateOnlyAsLocal } from "../../utilities/CommonUtilities";
import EmLoader from "../Common/EmLoader";
import { FetchTopGainersAndLosersRequest } from "./DashboardSerializer";

export const createEmptySelectedFilters = () => ({
  filter1: null,
  filter2: [],
  filter3: [],
});

const normalizeSelectedFilterArray = (value) => {
  if (Array.isArray(value)) {
    return value.filter((item) => item !== null && item !== undefined && item !== "");
  }

  if (value === null || value === undefined || value === "") {
    return [];
  }

  return [value];
};

const normalizeSelectedFiltersShape = (selectedFilters) => ({
  filter1: selectedFilters?.filter1 || null,
  filter2: normalizeSelectedFilterArray(selectedFilters?.filter2),
  filter3: normalizeSelectedFilterArray(selectedFilters?.filter3),
});

export const getStoredSelectedFilters = (localStorageKey) => {
  if (typeof localStorage === "undefined" || !localStorageKey) {
    return createEmptySelectedFilters();
  }

  const savedFilters = localStorage.getItem(localStorageKey);
  if (!savedFilters) {
    return createEmptySelectedFilters();
  }

  try {
    return normalizeSelectedFiltersShape(JSON.parse(savedFilters));
  } catch (error) {
    console.error("Error parsing saved dashboard filters:", error);
    localStorage.removeItem(localStorageKey);
    return createEmptySelectedFilters();
  }
};

const toTitleCaseLabel = (value) =>
  String(value)
    .split(/[_ ]+/)
    .filter(Boolean)
    .map((word) => word.charAt(0).toUpperCase() + word.slice(1).toLowerCase())
    .join(" ");

const createScoreOption = (value) => ({
  value,
  label: toTitleCaseLabel(value),
});

const createCountryOption = (value) => ({
  value,
  label: toTitleCaseLabel(value),
});

const createCompanyOption = (value) => ({
  value,
  label: value,
});

const getOptionValue = (option) =>
  typeof option === "string" ? option : option?.value;

const findCaseInsensitiveMatch = (values, candidate) => {
  const normalizedCandidate = String(candidate || "").trim().toLowerCase();

  if (!normalizedCandidate) {
    return null;
  }

  return (
    values.find(
      (value) => String(value).trim().toLowerCase() === normalizedCandidate
    ) || null
  );
};

const getDefaultSelectedFilters = (filterData) => {
  const firstScore = Object.keys(filterData)[0];
  if (!firstScore) {
    return createEmptySelectedFilters();
  }

  const firstCountry = Object.keys(filterData[firstScore] || {}).sort()[0];
  const firstCompany = firstCountry
    ? (filterData[firstScore][firstCountry] || [])[0]
    : null;

  return {
    filter1: createScoreOption(firstScore),
    filter2: firstCountry ? [createCountryOption(firstCountry)] : [],
    filter3: firstCompany ? [createCompanyOption(firstCompany)] : [],
  };
};

const reconcileSelectedFilters = ({ filterData, selectedFilters }) => {
  const defaultSelectedFilters = getDefaultSelectedFilters(filterData);
  const normalizedSelectedFilters = normalizeSelectedFiltersShape(selectedFilters);
  const requestedScore = getOptionValue(normalizedSelectedFilters.filter1);

  // No selection at all: the defaults. Countries or themes without a score
  // (e.g. from an embed launch) are matched under the first score.
  if (
    !requestedScore &&
    normalizedSelectedFilters.filter2.length === 0 &&
    normalizedSelectedFilters.filter3.length === 0
  ) {
    return {
      nextSelectedFilters: defaultSelectedFilters,
      shouldResetStoredFilters: false,
    };
  }

  const availableScores = Object.keys(filterData);
  const matchedScore = requestedScore
    ? findCaseInsensitiveMatch(availableScores, requestedScore)
    : availableScores[0];

  if (!matchedScore) {
    return {
      nextSelectedFilters: defaultSelectedFilters,
      shouldResetStoredFilters: true,
    };
  }

  let shouldResetStoredFilters = false;

  const availableCountries = Object.keys(filterData[matchedScore] || {}).sort();
  const matchedCountries = normalizedSelectedFilters.filter2
    .map((country) =>
      findCaseInsensitiveMatch(availableCountries, getOptionValue(country))
    )
    .filter(Boolean);

  let nextCountries = Array.from(new Set(matchedCountries));

  if (normalizedSelectedFilters.filter2.length === 0) {
    shouldResetStoredFilters = true;
  }

  if (matchedCountries.length !== normalizedSelectedFilters.filter2.length) {
    shouldResetStoredFilters = true;
  }

  if (nextCountries.length === 0 && availableCountries.length > 0) {
    nextCountries = [availableCountries[0]];
    shouldResetStoredFilters = true;
  }

  const availableCompanies = nextCountries.flatMap(
    (country) => filterData[matchedScore][country] || []
  );
  const matchedCompanies = normalizedSelectedFilters.filter3
    .map((company) =>
      findCaseInsensitiveMatch(availableCompanies, getOptionValue(company))
    )
    .filter(Boolean);

  let nextCompanies = Array.from(new Set(matchedCompanies));

  if (normalizedSelectedFilters.filter3.length === 0) {
    shouldResetStoredFilters = true;
  }

  if (matchedCompanies.length !== normalizedSelectedFilters.filter3.length) {
    shouldResetStoredFilters = true;
  }

  if (nextCompanies.length === 0 && availableCompanies.length > 0) {
    nextCompanies = [availableCompanies[0]];
    shouldResetStoredFilters = true;
  }

  return {
    nextSelectedFilters: {
      filter1: createScoreOption(matchedScore),
      filter2: nextCountries.map(createCountryOption),
      filter3: nextCompanies.map(createCompanyOption),
    },
    shouldResetStoredFilters,
  };
};

export const fetchDashboardData = async ({
  fetchDashboardDataRequest,
  setDashboardData,
  setIsLoading,
  currentReqKeyRef,
  setDashboardChartData,
  filterDate,
  selectedFilters,
  // Lets callers drop responses superseded by a newer request, so an older,
  // slower response can never overwrite what the current filters show.
  isLatestRequest = () => true,
}) => {
  try {
    setIsLoading(true);
    const response = await fetchPutResponse(
      "dashboard/fetch_dashboard_data",
      fetchDashboardDataRequest
    );
    if (!response.ok) throw new Error("Network response was not ok.");
    const { data: apiData } = await response.json();
    if (
      isLatestRequest() &&
      currentReqKeyRef.current === fetchDashboardDataRequest.dashboard_name
    ) {
      if (Object.keys(apiData).length !== 0) {
        setDashboardData(apiData);
        await updateChartData({
          dashboardData: apiData,
          selectedFilters,
          setDashboardChartData,
          filterDate,
        });
      } else {
        setDashboardData(null);
        setDashboardChartData(null);
      }
    }
  } catch (error) {
    console.error("Error fetching data:", error);
  } finally {
    if (isLatestRequest()) setIsLoading(false);
  }
};

export const fetchDashboardFilter = async ({
  fetchDashboardFiltersRequest,
  setFilters,
  setSelectedFilters,
  selectedFilters,
  setIsLoading,
  currentReqKeyRef,
  localStorageKey,
  isLatestRequest = () => true,
}) => {
  try {
    setIsLoading(true);
    const response = await fetchPutResponse(
      "dashboard/fetch_dashboard_filters",
      fetchDashboardFiltersRequest
    );
    if (!response.ok) throw new Error("Network response was not ok.");
    const { data: apiData } = await response.json();
    if (
      isLatestRequest() &&
      currentReqKeyRef.current === fetchDashboardFiltersRequest.dashboard_name
    ) {
      if (Object.keys(apiData).length !== 0) {
        const { nextSelectedFilters, shouldResetStoredFilters } = setFilterData({
          apiData,
          setFilters,
          setSelectedFilters,
          selectedFilters,
        });

        if (
          shouldResetStoredFilters &&
          typeof localStorage !== "undefined" &&
          localStorageKey
        ) {
          localStorage.removeItem(localStorageKey);
        }

        return { nextSelectedFilters, shouldResetStoredFilters };
      } else {
        // setDashboardData(null);
      }
    }
  } catch (error) {
    console.error("Error fetching data:", error);
  } finally {
    if (isLatestRequest()) setIsLoading(false);
  }
};

const setFilterData = ({
  apiData,
  setFilters,
  setSelectedFilters,
  selectedFilters,
}) => {
  const filterData = apiData;
  const reconciledFilters = reconcileSelectedFilters({
    filterData,
    selectedFilters,
  });

  setSelectedFilters(reconciledFilters.nextSelectedFilters);
  setFilters(filterData);

  return reconciledFilters;
};

export const updateChartData = async ({
  dashboardData,
  selectedFilters,
  setDashboardChartData,
  filterDate,
}) => {
  try {

    const today = formatDate(new Date()); // Get today's date in 'YYYY-MM-DD' format
    const endDate = formatDate(filterDate.endDate); // Get endDate in 'YYYY-MM-DD' format
    let seriesData = Object.values(dashboardData) // Use Object.values for direct access to values
      .map((entry) => ({
        name: entry.company_name,
        country: entry.country_name,
        isin: entry.isin,
        tickerName: entry.ticker_name,
        priceTicker: entry.price_ticker || entry.ticker || "",
        data: entry.scores[selectedFilters["filter1"].value.toUpperCase()].map(
          (score, index) => [
            new Date(entry.dates[index] + "T00:00:00"),
            score === "None" ? null : Number(score),
            entry.isin,
          ]
        ),
      }))
      .filter(
        ({ name, country }) =>
          (selectedFilters["filter2"].map((x) => x.value).length === 0 ||
            selectedFilters["filter2"].map((x) => x.value).includes(country)) &&
          (selectedFilters["filter3"].map((x) => x.value).length === 0 ||
            selectedFilters["filter3"].map((x) => x.value).includes(name))
      );
    if (endDate === today) {
      const allZero = seriesData.every(
        ({ data }) => data[data.length - 1][1] === 0
      );
      if (allZero) {
        seriesData = seriesData.map((entry) => ({
          ...entry,
          data: entry.data.slice(0, -1),
        }));
      }
    }
    setDashboardChartData(seriesData);
  } catch (error) {
    console.error("Error fetching data:", error);
  } finally {
    // setIsLoading(false);
  }
};
export function capitalize(str) {
  return str
    .split(" ")
    .map((word) => word.charAt(0).toUpperCase() + word.slice(1).toLowerCase())
    .join(" ");
}
export const fetchDashboardDataHome = async ({
  fetchDashboardDataRequest,
  default_country_filter,
}) => {
  try {
    const response = await fetchPutResponse(
      "dashboard/fetch_dashboard_data",
      fetchDashboardDataRequest
    );
    if (!response.ok) throw new Error("Network response was not ok.");
    const { data: apiData } = await response.json();
    if (Object.keys(apiData).length !== 0) {
      const { firstScore, firstCountry } = getFirstFilter(apiData);
      return Object.values(apiData) // Use Object.values for direct access to values
        .map((entry) => ({
          name: entry.company_name,
          country: entry.country_name,
          isin: entry.isin,
          data: entry.scores[firstScore].map((score, index) => [
            parseDateOnlyAsLocal(entry.dates[index]).getTime(),
            score === "None" ? null : Number(score),
            entry.isin,
          ]),
        }))
        .filter(({ name, country }) =>
          [default_country_filter || firstCountry].includes(country)
        )
        .slice(0, 20);
    } else {
      return null;
    }
  } catch (error) {
    console.error("Error fetching data:", error);
  }
};

export const getFirstFilter = (apiData) => {
  const filterData = {};
  Object.values(apiData).forEach((entry) => {
    const companyName = entry.company_name;
    const countryName = entry.country_name;
    const isin = entry.isin;
    const scores = Object.keys(entry.scores);
    scores.forEach((score) => {
      if (!filterData[score]) {
        filterData[score] = {};
      }
      if (!filterData[score][countryName]) {
        filterData[score][countryName] = {};
      }
      if (!filterData[score][countryName][companyName]) {
        filterData[score][countryName][companyName] = {};
      }
      filterData[score][countryName][companyName] = isin;
    });
  });
  const firstScore =Object.keys(filterData).filter(
    (x) => {const key = x?.toLowerCase();
    return key.includes("normal") || key.includes("smooth");})[0];
  const firstCountry = Object.keys(filterData[firstScore]).sort()[0];
  return { firstScore, firstCountry };
};

export const getDefaultDates = (subTabs, dashboard_name) => {
  const today = new Date();
  const todayDate = new Date(today.setHours(0, 0, 0, 0));
  const fallbackEndDate = new Date(todayDate);
  const fallbackStartDate = new Date(todayDate);
  fallbackStartDate.setMonth(fallbackStartDate.getMonth() - 2);

  const defaultEndDate =
    subTabs
      .filter((x) => x.dashboardName === dashboard_name)
      .map((x) =>
        x.default_end_date
          ? parseDateOnlyAsLocal(x.default_end_date)
          : new Date(fallbackEndDate)
      )[0] || new Date(fallbackEndDate);

  const defaultStartDate =
    subTabs
      .filter((x) => x.dashboardName === dashboard_name)
      .map((x) =>
        x.default_start_date
          ? parseDateOnlyAsLocal(x.default_start_date)
          : new Date(fallbackStartDate)
      )[0] || new Date(fallbackStartDate);
  const filter_companies = subTabs
    .filter((x) => x.dashboardName === dashboard_name)
    .map((x) => x.filter_companies)[0];
  const filter_countries = subTabs
    .filter((x) => x.dashboardName === dashboard_name)
    .map((x) => x.filter_countries)[0];
  const default_embedding = subTabs
    .filter((x) => x.dashboardName === dashboard_name)
    .map((x) => x.default_embedding);
  const start_date_limit = subTabs
    .filter((x) => x.dashboardName === dashboard_name)
    .map((x) => x.start_date_limit)[0];
  const ner_relevance_cutoff = subTabs
    .filter((x) => x.dashboardName === dashboard_name)
    .map((x) => x.ner_relevance_cutoff)[0];
  const dashboard_description = subTabs
    .filter((x) => x.dashboardName === dashboard_name)
    .map((x) => x.dashboard_description)[0];
  const enable_price_overlay = subTabs
    .filter((x) => x.dashboardName === dashboard_name)
    .map((x) => x.enable_price_overlay)[0];
  const point_click_news_lookback_days = subTabs
    .filter((x) => x.dashboardName === dashboard_name)
    .map((x) => x.point_click_news_lookback_days)[0];
  const default_granularity = subTabs
    .filter((x) => x.dashboardName === dashboard_name)
    .map((x) => x.default_granularity)[0];
  const end_date_limit = subTabs
    .filter((x) => x.dashboardName === dashboard_name)
    .map((x) => x.end_date_limit)[0];
  const available_granularities = subTabs
    .filter((x) => x.dashboardName === dashboard_name)
    .map((x) => x.available_granularities)[0];
  return {
    defaultStartDate,
    defaultEndDate,
    filter_companies,
    filter_countries,
    default_embedding,
    start_date_limit,
    end_date_limit,
    ner_relevance_cutoff,
    dashboard_description,
    enable_price_overlay,
    point_click_news_lookback_days,
    default_granularity,
    available_granularities
  };
};

export const getSinglePriceOverlayTarget = (dashboardChartData) => {
  const seriesByIsin = new Map();

  (dashboardChartData || []).forEach((series) => {
    const isin = String(series?.isin || "").trim();
    if (isin && !seriesByIsin.has(isin)) {
      seriesByIsin.set(isin, series);
    }
  });

  if (seriesByIsin.size !== 1) {
    return null;
  }

  const [isin, series] = seriesByIsin.entries().next().value;
  const priceTicker = String(series?.priceTicker || "").trim();
  const tickerName = String(series?.tickerName || "").trim();
  const seriesName = String(series?.name || "").trim();

  if (!priceTicker) {
    return null;
  }

  return {
    isin,
    label: tickerName || seriesName || priceTicker,
    priceTicker,
  };
};

export const getPriceOverlayDateRange = (filterDate = {}) => {
  const startDate = new Date(filterDate.startDate || new Date());
  const endDate = new Date(filterDate.endDate || new Date());

  startDate.setHours(0, 0, 0, 0);
  endDate.setHours(23, 59, 59, 999);

  return {
    startDate: startDate.toISOString(),
    endDate: endDate.toISOString(),
  };
};

export const initializeFilters = (
  setDashboardData,
  setFilters,
  setDashboardChartData,
  setNewsList,
  setDocumentIds,
  SetOffset
) => {
  setDashboardData(null);
  setFilters({});
  setDashboardChartData(null);
  setNewsList([]);
  setDocumentIds([]);
  SetOffset(0);
};

export const renderLoadingOrNoData = (
  isLoading,
  dashboardChartData,
  dashboardName
) => {
  if (isLoading) {
    return (
      <div className="w-100 h-100 d-flex justify-content-center align-items-center">
        <EmLoader size="lg" label="Loading chart" />
      </div>
    );
  }

  if (
    dashboardChartData === null ||
    (Array.isArray(dashboardChartData) && dashboardChartData.length === 0)
  ) {
    return (
      <div className="w-100 h-100 d-flex justify-content-center align-items-center">
        <div className="text-center">
          <p className="alert alert-warning m-0" role="alert">
            No data available for the selected Date. Please select another date
            range.
          </p>
        </div>
      </div>
    );
  }

  return null;
};

export const downloadDashboardData = async ({
  fetchDashboardDataRequest,
  setIsLoading,
}) => {
  try {
    setIsLoading(true);

    const response = await fetchPutResponse(
      "dashboard/download_dashboard_data",
      fetchDashboardDataRequest
    );

    if (!response.ok) throw new Error("Network response was not ok.");

    // Determine the filename and content type
    const contentType = response.headers.get("Content-Type");
    let fileExtension = "txt";

    if (contentType.includes("application/json")) {
      fileExtension = "json";
    } else if (contentType.includes("text/csv")) {
      fileExtension = "csv";
    } else if (
      contentType.includes(
        "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet"
      )
    ) {
      fileExtension = "xlsx";
    }

    // Create a Blob from the response stream
    const blob = await response.blob();
    const url = window.URL.createObjectURL(blob);

    // Create a download link and trigger the download
    const a = document.createElement("a");
    a.href = url;
    a.download = `Dashboard_Data_${fetchDashboardDataRequest.start_date}_${fetchDashboardDataRequest.end_date}.${fileExtension}`;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    window.URL.revokeObjectURL(url);
  } catch (error) {
    console.error("Error downloading file:", error);
  } finally {
    setIsLoading(false);
  }
};

export const extractTopSentiments = async (
  dashboardName,
  scoreKey = "SENTIMENT_SCORE"
) => {

  const response = await fetchPutResponse(
      "dashboard/fetch_top_gainer_and_loser",
      new FetchTopGainersAndLosersRequest(
        dashboardName,
        scoreKey
      )
    );
     if (!response.ok) throw new Error("Network response was not ok.");
    const { data } = await response.json();
    const key = scoreKey.toLowerCase()
    const topPositives = data.top_gainers.map((entry)=>{
        return {company: entry.company_name,
        country: entry.country_name,
        isin: entry.isin,
        date: entry.data_timestamp,
        score: entry[key]}
    })
    
    const topNegatives = data.top_losers.map((entry)=>{
        return {
        company: entry.company_name,
        country: entry.country_name,
        isin: entry.isin,
        date: entry.data_timestamp,
        score: entry[key]}
    })
    return { topPositives, topNegatives };
  
};
